/* Charts view: bucket donut, bucket bars with a metric toggle, top conversation stages,
   and where conversational demand ranks. All SVG/markup ported verbatim from
   artifact.html; the numbers now come from the aggregate endpoints.
   Every segment, bar and legend row filters the query table. */

import { $, esc, fmt } from "../util.js";
import { BCOLOR, ORDER, STAGE_FAM } from "../colors.js";

let ovMetric = "n";

export function initChartToggle(onChange) {
  document.querySelectorAll("#ov-toggle button").forEach(b => b.addEventListener("click", () => {
    document.querySelectorAll("#ov-toggle button").forEach(x => x.classList.remove("on"));
    b.classList.add("on");
    ovMetric = b.dataset.m;
    onChange();
  }));
}

export function renderCharts({ buckets, stages, bands }, ctx) {
  const total = buckets.reduce((n, b) => n + b.n, 0) || 1;
  const byBucket = new Map(buckets.map(b => [b.value, b]));

  /* donut */
  const segs = ORDER.filter(b => byBucket.has(b)).map(b => [b, byBucket.get(b).n]);
  let off = 25, svg = "";
  for (const [b, n] of segs) {
    const pct = n / total * 100;
    svg += '<circle class="donut-seg" data-db="' + b + '" cx="21" cy="21" r="15.9155" fill="none" stroke="' + BCOLOR[b] +
      '" stroke-width="5" stroke-dasharray="' + pct + " " + (100 - pct) + '" stroke-dashoffset="' + off + '"><title>' + b + ": " + fmt(n) + "</title></circle>";
    off -= pct;
  }
  const convPct = ((byBucket.get("conversational")?.n || 0) / total * 100).toFixed(1);
  svg += '<text x="21" y="20" text-anchor="middle" font-family="JetBrains Mono" font-weight="800" font-size="7" fill="#0A0A0B">' + convPct +
    '%</text><text x="21" y="26.5" text-anchor="middle" font-family="Figtree" font-weight="600" font-size="3" fill="#57575F">CONVERSATIONAL</text>';
  $("donut").innerHTML = svg;

  $("donut-legend").innerHTML = segs.map(([b, n]) =>
    '<div style="display:flex;align-items:center;gap:7px;font-size:11.5px;color:var(--sub);cursor:pointer" data-db="' + b + '"><span style="width:9px;height:9px;border-radius:3px;background:' + BCOLOR[b] +
    ';flex-shrink:0"></span>' + b + '<span style="margin-left:auto;font-family:var(--mono)"><b>' + fmt(n) + "</b> · " +
    (n / total * 100).toFixed(1) + "% · " + fmt(byBucket.get(b).imp) + " impr</span></div>").join("");

  document.querySelectorAll("[data-db]").forEach(el => el.addEventListener("click", () => ctx.onFilter({ bucket: el.dataset.db })));

  /* bucket bars, by the selected metric */
  const rows2 = buckets.map(b => [b.value, b[ovMetric]]).sort((a, b) => b[1] - a[1]);
  const mx2 = Math.max(...rows2.map(r => r[1]), 1);
  $("bucket-bars").innerHTML = rows2.map(([b, v]) =>
    '<div class="brow"><span class="bl" data-db2="' + b + '">' + b + '</span><div class="btrack"><div class="bfill" style="width:' +
    (v / mx2 * 100) + "%;background:" + BCOLOR[b] + '"></div></div><span class="bv">' + fmt(v) + "</span></div>").join("");
  document.querySelectorAll("[data-db2]").forEach(el => el.addEventListener("click", () => ctx.onFilter({ bucket: el.dataset.db2 })));

  /* top conversation stages, coloured by family */
  const smx = Math.max(...stages.map(s => s.n), 1);
  $("stage-bars").innerHTML = stages.map(s => {
    const fam = STAGE_FAM[s.value] || ["var(--sub)", "#F1F1F4"];
    return '<div class="brow"><span class="bl" data-ds="' + esc(s.value) + '" title="' + esc(s.value) + '">' + esc(s.value) +
      '</span><div class="btrack"><div class="bfill" style="width:' + (s.n / smx * 100) + "%;background:" + fam[0] +
      '"></div></div><span class="bv">' + fmt(s.n) + "</span></div>";
  }).join("") || '<div style="font-size:12px;color:var(--muted)">No staged queries.</div>';
  document.querySelectorAll("[data-ds]").forEach(el => el.addEventListener("click", () => ctx.onFilter({ stage: el.dataset.ds })));

  /* where conversational demand ranks */
  const BAND_COLORS = { "Top 3": "#16A34A", "Pos 4-10": "#2563EB", "Beyond 10": "#DC2626" };
  const totBand = bands.reduce((n, b) => n + b.n, 0) || 1;
  $("pos-stack").innerHTML = '<div style="display:flex;height:16px;border-radius:6px;overflow:hidden">' +
    bands.map(b => b.n ? '<div style="width:' + (b.n / totBand * 100) + "%;background:" + BAND_COLORS[b.band] + '" title="' + b.band + ": " + b.n + '"></div>' : "").join("") + "</div>";
  const bmx = Math.max(...bands.map(b => b.n), 1);
  $("pos-bars").innerHTML = bands.map(b =>
    '<div class="brow" style="grid-template-columns:110px 1fr 110px"><span class="bl">' + b.band.replace("Pos 4-10", "Pos 4–10") +
    '</span><div class="btrack"><div class="bfill" style="width:' + (b.n / bmx * 100) + "%;background:" + BAND_COLORS[b.band] +
    '"></div></div><span class="bv">' + fmt(b.n) + " q · " + fmt(b.imp) + " im</span></div>").join("");
}

export const chartMetric = () => ovMetric;
